import { computed, markRaw, ref } from 'vue'

import { defineStore } from 'pinia'

import { MERMAID_AUTO_FIX_MAX } from './constants'
import {
  buildMermaidFixUserMessage,
  clipTrailingAfterHtmlClose,
  extractLastMermaidCodeBlock,
  isSamePreviewSession,
} from './helpers'

import type { PreviewContent, PreviewContentType } from './types'

export type {
  HtmlPublishKey,
  PreviewContent,
  PreviewContentType,
} from './types'

export {
  MERMAID_AUTO_FIX_MAX,
  HTML_FENCE_GLOBAL_RE,
  HTML_OR_MERMAID_PREVIEW_FENCE_RE,
  HTML_PREVIEW_FENCE_INDEX_RE,
  HTML_PREVIEW_FENCE_RE,
  MERMAID_FENCE_RE,
} from './constants'

export {
  buildMermaidFixUserMessage,
  buildPreviewRendererAttrs,
  clipTrailingAfterHtmlClose,
  extractTrailingProseAfterHtmlClose,
  countClosedHtmlFences,
  countClosedMermaidFences,
  detectContentType,
  extractHtmlBlocks,
  extractLastMermaidCodeBlock,
  extractAllHtmlPreviewBlocks,
  extractHtmlPreviewBlocksWithIndex,
  extractLatestHtmlPreviewBlockStreaming,
  extractLatestHtmlPreviewBlockWithIndex,
  extractLatestPreviewBlockFromMarkdown,
  isGeneratedAssetGenerating,
  isGeneratedAssetIncomplete,
  isHtmlBlockGenerating,
  isHtmlGenerationComplete,
  isSamePreviewSession,
  previewPanelContentTitle,
  resolveHtmlPreviewViewMode,
} from './helpers'

export type { HtmlPreviewViewMode } from './helpers'

export const usePreviewStore = defineStore('preview', () => {
  const isOpen = ref(false)
  const content = ref<PreviewContent | null>(null)

  const contentType = computed<PreviewContentType | undefined>(
    () => content.value?.type,
  )
  const rawContent = computed<string>(() => {
    const c = content.value
    if (
      !c ||
      c.type === 'spreadsheet' ||
      c.type === 'powerpoint' ||
      c.type === 'pdf'
    )
      return ''
    if (c.type === 'html' || c.type === 'mermaid') return c.raw
    return ''
  })
  const htmlContent = computed<string>(() =>
    contentType.value === 'html' ? rawContent.value : '',
  )

  const htmlViewMode = ref<'preview' | 'code'>('preview')

  const setHtmlViewMode = (mode: 'preview' | 'code') => {
    htmlViewMode.value = mode
  }

  /**
   * Set when an HTML asset card asks to publish (its "Publish to Web" menu action):
   * the card seeds this block via `openPreview`, then raises this flag so the just-
   * mounted `PreviewPanel` opens the publish modal on top. A flag (not an event) so
   * the request survives the panel mounting after `isOpen` flips — the panel reads it
   * with an `immediate` watch and clears it via `clearPendingPublishRequest`.
   */
  const pendingPublishRequest = ref(false)

  const requestPublish = () => {
    pendingPublishRequest.value = true
  }

  const clearPendingPublishRequest = () => {
    pendingPublishRequest.value = false
  }

  /** Updates open HTML preview after POST publish without reopening the panel. */
  const setOpenPreviewPublishedHtmlUrl = (apiUrl: string) => {
    const c = content.value
    if (!c || c.type !== 'html') return
    content.value = { ...c, publishedHtmlApiUrl: apiUrl }
  }

  const updateHtmlPreview = (
    partial: Partial<Extract<PreviewContent, { type: 'html' }>>,
  ) => {
    const c = content.value
    if (!c || c.type !== 'html') return
    content.value = { ...c, ...partial }
  }

  /** Increments when Chat sends a Mermaid fix message; blocks further Fix chart requests after MERMAID_AUTO_FIX_MAX. */
  const mermaidAutoFixSentCount = ref(0)

  /** User message queued when the user clicks Fix chart; Chat.vue sends it when the chat is idle. */
  const pendingMermaidFixRequest = ref<string | null>(null)

  /** True after Fix chart queues a message until the assistant finishes (see `mermaidFixSubmitted`). */
  const mermaidFixAwaitingResponse = ref(false)
  /** True once Chat has sent the fix request; used so we only clear loading when that turn completes. */
  const mermaidFixSubmitted = ref(false)

  const clearMermaidFixLoading = () => {
    mermaidFixAwaitingResponse.value = false
    mermaidFixSubmitted.value = false
  }

  const resetMermaidAutoFixCount = () => {
    mermaidAutoFixSentCount.value = 0
  }

  /** Full reset of Mermaid fix queue / loading (new preview, close, or successful render count only via resetMermaidAutoFixCount). */
  const resetMermaidFixSession = () => {
    resetMermaidAutoFixCount()
    pendingMermaidFixRequest.value = null
    clearMermaidFixLoading()
  }

  const openPreview = (payload: PreviewContent) => {
    // Strip any trailing prose the model added after `</html>` so it never renders
    // in the iframe or gets published — the single chokepoint every open flows
    // through (stream auto-preview, inline card, selector).
    const normalized =
      payload.type === 'html'
        ? { ...payload, raw: clipTrailingAfterHtmlClose(payload.raw) }
        : payload
    if (!isSamePreviewSession(content.value, normalized)) {
      resetMermaidFixSession()
      if (normalized.type === 'html') {
        htmlViewMode.value = 'preview'
      }
    }
    // Keep PPTX / PDF bytes out of deep reactivity (Vue proxies are unsafe for ArrayBuffer consumers).
    if (
      (normalized.type === 'powerpoint' || normalized.type === 'pdf') &&
      normalized.buffer
    ) {
      content.value = { ...normalized, buffer: markRaw(normalized.buffer) }
    } else {
      content.value = normalized
    }
    isOpen.value = true
  }

  const closePreview = () => {
    isOpen.value = false
    content.value = null
    htmlViewMode.value = 'preview'
    pendingPublishRequest.value = false
    resetMermaidFixSession()
  }

  const markMermaidAutoFixSent = () => {
    mermaidAutoFixSentCount.value += 1
  }

  const queueMermaidFixRequest = (error: string, source: string) => {
    if (mermaidAutoFixSentCount.value >= MERMAID_AUTO_FIX_MAX) {
      return
    }
    pendingMermaidFixRequest.value = buildMermaidFixUserMessage(error, source)
    mermaidFixAwaitingResponse.value = true
  }

  const markMermaidFixSubmitted = () => {
    mermaidFixSubmitted.value = true
  }

  const clearPendingMermaidFixRequest = () => {
    pendingMermaidFixRequest.value = null
  }

  /**
   * After a Fix chart reply, if the latest assistant message contains a mermaid fence, show it in preview.
   */
  const applyMermaidFixPreviewFromMessages = (
    messages: ReadonlyArray<{ role: string; content: string }>,
  ) => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role !== 'assistant') continue
      const raw = extractLastMermaidCodeBlock(messages[i].content)
      if (raw) {
        openPreview({ type: 'mermaid', raw })
      }
      return
    }
  }

  return {
    isOpen,
    content,
    contentType,
    rawContent,
    htmlContent,
    htmlViewMode,
    setHtmlViewMode,
    setOpenPreviewPublishedHtmlUrl,
    updateHtmlPreview,
    pendingPublishRequest,
    requestPublish,
    clearPendingPublishRequest,
    openPreview,
    closePreview,
    pendingMermaidFixRequest,
    queueMermaidFixRequest,
    clearPendingMermaidFixRequest,
    mermaidAutoFixSentCount,
    resetMermaidAutoFixCount,
    markMermaidAutoFixSent,
    mermaidFixAwaitingResponse,
    mermaidFixSubmitted,
    markMermaidFixSubmitted,
    clearMermaidFixLoading,
    applyMermaidFixPreviewFromMessages,
  }
})
